import { useSite } from '../context/SiteContext';
import { toBn } from '../lib/utils';
import { CLOSED_LABEL, discountPercent, taka } from '../lib/price';
import { ExcelMockup, ResultMockup } from './AppMockups';
import { ArrowRightIcon, CalendarIcon, CheckIcon, VideoIcon } from './Icons';

export default function Hero() {
  const { course } = useSite();
  const discount = discountPercent(course);
  const hasPrice = course.price != null;

  return (
    <section id="top" className="relative overflow-hidden bg-gradient-to-b from-word-50 via-white to-white">
      <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-excel-100/70 blur-3xl" />
      <div className="pointer-events-none absolute -left-40 top-40 h-96 w-96 rounded-full bg-word-100/80 blur-3xl" />

      <div className="container-x relative grid items-center gap-12 pb-16 pt-10 lg:grid-cols-2 lg:pb-24 lg:pt-16">
        <div>
          <span className="eyebrow">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
            </span>
            লাইভ অনলাইন কোর্স{course.batchName && ` · ${course.batchName}`}
          </span>

          <h1 className="mt-5 text-4xl font-bold leading-[1.2] text-slate-900 sm:text-5xl lg:text-[3.4rem]">
            মাত্র <span className="text-word-500">{toBn(course.totalClasses)}টি ক্লাসে</span>{' '}
            <span className="relative whitespace-nowrap text-excel-500">
              MS Excel
              <svg className="absolute bottom-[0.3em] left-0 w-full" viewBox="0 0 200 12" fill="none" aria-hidden="true">
                <path d="M2 9c50-6 146-8 196-3" stroke="#f97316" strokeWidth="4" strokeLinecap="round" />
              </svg>
            </span>{' '}
            শিখুন <span className="whitespace-nowrap">হাতে-কলমে</span>
          </h1>

          <p className="mt-6 max-w-xl text-lg leading-relaxed text-slate-600">
            শিক্ষার্থী ও অফিস কর্মীদের জন্য বানানো প্র্যাকটিক্যাল Excel কোর্স। SUM, IF, VLOOKUP থেকে Salary Sheet আর Pivot Table — অফিসে প্রতিদিন যা লাগে,{' '}
            <strong className="text-slate-800">{course.instructor.name}</strong>-এর সাথে সহজ বাংলায় শিখুন।
          </p>

          {hasPrice && (
            <div className="mt-7 flex flex-wrap items-end gap-x-4 gap-y-2">
              <div className="text-5xl font-bold text-slate-900">{taka(course.price)}</div>
              {discount && <div className="pb-1.5 text-xl text-slate-400 line-through">{taka(course.regularPrice)}</div>}
              {discount && (
                <span className="mb-2 rounded-full bg-red-100 px-3 py-1 text-sm font-bold text-red-600">{toBn(discount)}% ছাড়</span>
              )}
            </div>
          )}

          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            {course.enrollmentOpen ? (
              <a href="#enroll" className="btn-cta">
                এখনই সিট বুক করুন <ArrowRightIcon className="h-5 w-5" />
              </a>
            ) : (
              <span className="btn-closed">{CLOSED_LABEL}</span>
            )}
            <a href="#curriculum" className="btn-ghost">
              কী কী শিখবেন দেখুন
            </a>
          </div>

          <ul className="mt-8 grid gap-3 text-slate-700 sm:grid-cols-3">
            <li className="flex items-center gap-2">
              <VideoIcon className="h-5 w-5 text-word-500" /> {toBn(course.totalClasses)}টি লাইভ ক্লাস
            </li>
            <li className="flex items-center gap-2">
              <CalendarIcon className="h-5 w-5 text-word-500" /> একদিন পর পর ক্লাস
            </li>
            <li className="flex items-center gap-2">
              <CheckIcon className="h-5 w-5 text-excel-500" /> {toBn(course.windowDays)} দিনে সম্পন্ন
            </li>
          </ul>
        </div>

        <div className="relative mx-auto w-full max-w-lg lg:max-w-none">
          <div className="relative ml-auto w-[88%] -rotate-2 animate-float-slow">
            <ResultMockup />
          </div>
          <div className="relative -mt-24 w-[88%] rotate-1 animate-float sm:-mt-32">
            <ExcelMockup />
          </div>

          {hasPrice && (
            <div className="absolute -left-2 top-6 hidden rounded-2xl bg-white px-4 py-3 shadow-xl ring-1 ring-slate-900/5 sm:block">
              <div className="text-xs text-slate-500">মাত্র</div>
              <div className="text-2xl font-bold text-cta-500">{taka(course.price)}</div>
            </div>
          )}
          <div className="absolute -bottom-6 right-6 hidden items-center gap-2 rounded-2xl bg-white px-4 py-3 shadow-xl ring-1 ring-slate-900/5 sm:flex">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-excel-100 text-lg">🎓</span>
            <div>
              <div className="text-sm font-bold text-slate-800">সার্টিফিকেট সহ</div>
              <div className="text-xs text-slate-500">কোর্স শেষে</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
